import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Table,
  Tag,
  Progress,
  Badge,
  Space,
  Segmented,
  Drawer,
  Button,
  Grid
} from 'antd';
import {
  ShoppingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PieChartOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { FaPizzaSlice, FaHamburger, FaCoffee, FaIceCream, FaWineGlassAlt } from 'react-icons/fa';
import Api from '../api/Api';
import formatarValor from '../formatarValor';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const DashboardRestaurante = () => {
  const [produtosMaisPedidos, setProdutosMaisPedidos] = useState([]);
  const [ultimosPedidos, setUltimosPedidos] = useState([]);
  const [loading, setLoading] = useState({
    produtos: true,
    pedidos: true,
    stats: true
  });
  
  const [TotalPedido, setTotalPedido] = useState(0);
  const [Totalaberto, setTotalaberto] = useState(0);
  const [TotalFechado, setTotalFechado] = useState(0);
  const [TotalCancelado, setTotalCancelado] = useState(0);
  const [TotalProduto, setTotalProduto] = useState(0);
  const [semEstoque, setSemEstoque] = useState(0);
  const [Disponivel, setDisponivel] = useState(0);
  const [Indisponivel, setIndisponivel] = useState(0);
  
  const [activeView, setActiveView] = useState('stats');
  const [drawerVisible, setDrawerVisible] = useState(false);
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    document.title = "Dashboard";
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([
      fetchMaisVendidos(),
      fetchUltimosPedidos(),
      fetchDashboard()
    ]);
  };

  const fetchMaisVendidos = async () => {
    try {
      const response = await Api.MaisVendidos();
      if (Array.isArray(response)) {
        const formatado = response.map((item, index) => ({
          nome: item.descricao,
          key: item.id || item.id_produto || index,
          categoria: item.categoria,
          pedidos: item.total_pedida,
        }));
        setProdutosMaisPedidos(formatado);
      }
    } finally {
      setLoading(prev => ({ ...prev, produtos: false }));
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await Api.DasboardPedido();
      if (response && typeof response === "object") {
        setTotalPedido(response.pedidos || 0);
        setTotalaberto(response.aberto || 0);
        setTotalFechado(response.fechado || 0);
        setTotalCancelado(response.cancelado || 0);
        setIndisponivel(response.indidisponivel || 0);
        setDisponivel(response.disponivel || 0);
        setTotalProduto(response.produto || 0);
        setSemEstoque(response.semEstoque || 0);
      }
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  };

  const fetchUltimosPedidos = async () => {
    try {
      const response = await Api.UltimosPedidos();
      if (Array.isArray(response)) {
        // MANTENDO EXATAMENTE A MESMA ESTRUTURA DA VERSÃO ORIGINAL
        const formatado = response.map((item, index) => ({
          key: index,
          produto: item.produto,
          qtd: item.quantidade,
          mesa: item.mesa,
          status: item.status,
          statusText: item.status, // Mantendo statusText como na original
          total: item.total, 
          valor: item.preco
        }));
        setUltimosPedidos(formatado);
      }
    } finally {
      setLoading(prev => ({ ...prev, pedidos: false }));
    }
  };

  // Cores dos status - EXATAMENTE COMO NA VERSÃO ORIGINAL
  const statusColors = {
    ABERTO: 'blue',
    PENDENTE: 'orange',
    FECHADO: 'green',
    CANCELADO: 'red',
  };

  // COLUNAS EXATAMENTE COMO NA VERSÃO ORIGINAL
  const colunasPedidos = [
    {
      title: 'Mesa',
      dataIndex: 'mesa',
      key: 'mesa'
    },
    {
      title: 'Produto',
      dataIndex: 'produto',
      key: 'produto'
    },
    {
      title: 'Qtd',
      dataIndex: 'qtd',
      key: 'qtd'
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      render: (_, record) => {
        const valor = Number(record.valor ?? 0);
        return ` ${formatarValor(valor)}`;
      },
      align: 'right'
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (_, record) => {
        const total = Number(record.total ?? 0);
        return ` ${formatarValor(total)}`;
      },
      align: 'right'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      render: (status, record) => (
        <Tag color={statusColors[status]}>{record.statusText}</Tag>
      )
    },
  ];

  // Colunas de produtos mantidas
  const colunasProdutos = [
    {
      title: 'Produto',
      dataIndex: 'nome',
      key: 'nome',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 12, fontSize: '16px' }}>{record.icone}</span>
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.categoria}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Pedidos',
      dataIndex: 'pedidos',
      key: 'pedidos',
      render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>,
      align: 'right'
    }
  ];

  // Componente de Card de Estatística
  const StatCard = ({ title, value, icon, color, progress }) => (
    <Card
      variant="borderless"
      style={{
        borderRadius: 16,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        background: `linear-gradient(135deg, ${color[0]} 0%, ${color[1]} 100%)`,
        color: 'white',
        height: '100%'
      }}
      styles={{
        body: { 
          padding: isMobile ? '16px' : '24px',
          height: '100%'
        }
      }}
    >
      <Statistic
        title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? 12 : 14 }}>{title}</span>}
        value={value}
        valueStyle={{ 
          color: 'white', 
          fontSize: isMobile ? '24px' : '32px',
          fontWeight: 700
        }}
        prefix={icon}
      />
      {progress !== undefined && (
        <Progress 
          percent={progress} 
          showInfo={false} 
          strokeColor="rgba(255,255,255,0.4)"
          trailColor="rgba(255,255,255,0.2)"
          size={isMobile ? "small" : "default"}
          style={{ marginTop: 8 }}
        />
      )}
    </Card>
  );

  return (
    <div style={{ 
      padding: isMobile ? '12px' : '24px', 
      background: '#f5f5f5', 
      minHeight: '100vh' 
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isMobile ? 16 : 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <Title level={isMobile ? 4 : 2} style={{ 
            color: '#1a1a2e', 
            marginBottom: 0,
            fontSize: isMobile ? '1.5rem' : '2rem'
          }}>
            Dashboard Restaurante
          </Title>
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
            Visão geral do desempenho do restaurante
          </Text>
        </div>
        
        {isMobile && (
          <Button 
            icon={<FilterOutlined />} 
            shape="circle"
            onClick={() => setDrawerVisible(true)}
          />
        )}
      </div>

      {!isMobile ? (
        // LAYOUT DESKTOP - EXATAMENTE COMO A VERSÃO ORIGINAL MAS OTIMIZADO
        <>
          {/* Cards de Pedidos */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Total de Pedidos"
                value={TotalPedido}
                icon={<ShoppingOutlined />}
                color={['#ff6b6b', '#ff9e6b']}
                progress={100}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Pedidos Abertos"
                value={Totalaberto}
                icon={<ClockCircleOutlined />}
                color={['#36d1dc', '#5b86e5']}
                progress={(Totalaberto / TotalPedido) * 100}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Pedidos Cancelados"
                value={TotalCancelado}
                icon={<CloseCircleOutlined />}
                color={['#f5567b', '#fd746c']}
                progress={(TotalCancelado / TotalPedido) * 100}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Pedidos Fechados"
                value={TotalFechado}
                icon={<CheckCircleOutlined />}
                color={['#56ab2f', '#a8e063']}
                progress={(TotalFechado / TotalPedido) * 100}
              />
            </Col>
          </Row>

          {/* Cards de Produtos */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                variant="borderless"
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <Statistic
                  title="Total de Produtos"
                  value={TotalProduto}
                  valueStyle={{ color: '#1a1a2e' }}
                  prefix={<PieChartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                variant="borderless"
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <Statistic
                  title="Sem Estoque"
                  value={semEstoque}
                  valueStyle={{ color: '#f5222d' }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                variant="borderless"
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <Statistic
                  title="Disponíveis"
                  value={Disponivel}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                variant="borderless"
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <Statistic
                  title="Indisponíveis"
                  value={Indisponivel}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Tabelas - EXATAMENTE COMO NA VERSÃO ORIGINAL */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <FireOutlined style={{ color: '#ff6b6b' }} />
                    <Text strong>Produtos Mais Pedidos</Text>
                  </Space>
                }
                variant="borderless"
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <Table
                  dataSource={produtosMaisPedidos}
                  columns={colunasProdutos}
                  pagination={false}
                  size="middle"
                />
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <HistoryOutlined style={{ color: '#ff6b6b' }} />
                    <Text strong>Últimos Pedidos</Text>
                    <Badge count={ultimosPedidos.length} style={{ backgroundColor: '#ff6b6b' }} />
                  </Space>
                }
                variant="borderless"
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                {/* TABELA EXATAMENTE IGUAL À VERSÃO ORIGINAL */}
                <Table
                  dataSource={ultimosPedidos}
                  columns={colunasPedidos}
                  pagination={false}
                  size="middle"
                />
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        // LAYOUT MOBILE COM SEGMENTED CONTROL E TABELAS MANTIDAS
        <>
          <Segmented
            block
            value={activeView}
            onChange={setActiveView}
            options={[
              { label: 'Resumo', value: 'stats', icon: <AppstoreOutlined /> },
              { label: 'Produtos', value: 'produtos', icon: <FireOutlined /> },
              { label: 'Pedidos', value: 'pedidos', icon: <HistoryOutlined /> },
            ]}
            style={{ marginBottom: 16 }}
          />
          
          {activeView === 'stats' && (
            <Row gutter={[12, 12]}>
              <Col xs={12}>
                <StatCard
                  title="Total Pedidos"
                  value={TotalPedido}
                  icon={<ShoppingOutlined />}
                  color={['#ff6b6b', '#ff9e6b']}
                  progress={100}
                />
              </Col>
              <Col xs={12}>
                <StatCard
                  title="Em Aberto"
                  value={Totalaberto}
                  icon={<ClockCircleOutlined />}
                  color={['#36d1dc', '#5b86e5']}
                  progress={(Totalaberto / TotalPedido) * 100}
                />
              </Col>
              <Col xs={12}>
                <StatCard
                  title="Cancelados"
                  value={TotalCancelado}
                  icon={<CloseCircleOutlined />}
                  color={['#f5567b', '#fd746c']}
                  progress={(TotalCancelado / TotalPedido) * 100}
                />
              </Col>
              <Col xs={12}>
                <StatCard
                  title="Fechados"
                  value={TotalFechado}
                  icon={<CheckCircleOutlined />}
                  color={['#56ab2f', '#a8e063']}
                  progress={(TotalFechado / TotalPedido) * 100}
                />
              </Col>
              <Col xs={24}>
                <Card title="Inventário" size="small" style={{ borderRadius: 12 }}>
                  <Row justify="space-around">
                    <Col><Statistic title="Total" value={TotalProduto} prefix={<PieChartOutlined />} /></Col>
                    <Col><Statistic title="Disp." value={Disponivel} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Col>
                    <Col><Statistic title="Sem Est." value={semEstoque} valueStyle={{ color: '#ff4d4f' }} prefix={<ExclamationCircleOutlined />} /></Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
          
          {activeView === 'produtos' && (
            <Card
              title={<Space><FireOutlined style={{ color: '#ff6b6b' }} /><Text strong>Produtos Mais Pedidos</Text></Space>}
              style={{ borderRadius: 12 }}
            >
              <Table
                dataSource={produtosMaisPedidos}
                columns={colunasProdutos}
                pagination={false}
                size="small"
                scroll={{ x: 300 }}
              />
            </Card>
          )}
          
          {activeView === 'pedidos' && (
            <Card
              title={
                <Space>
                  <HistoryOutlined style={{ color: '#ff6b6b' }} />
                  <Text strong>Últimos Pedidos</Text>
                  <Badge count={ultimosPedidos.length} style={{ backgroundColor: '#ff6b6b' }} />
                </Space>
              }
              style={{ borderRadius: 12 }}
            >
              {}
              <Table
                dataSource={ultimosPedidos}
                columns={colunasPedidos}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }} // Permite scroll horizontal no mobile
              />
            </Card>
          )}
          
          <Drawer
            title="Filtros"
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width={280}
          >
            <p>Opções de filtro...</p>
          </Drawer>
        </>
      )}
    </div>
  );
};

export default DashboardRestaurante;